import { AuthService } from './authService';
import {
  SystemRuleGroupMaintenanceDto,
  CreateSystemRuleGroupRequest,
  UpdateSystemRuleGroupRequest,
} from '../types/systemRuleGroupMaintenance';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class SystemRuleGroupMaintenanceService {
  private baseUrl = `${API_BASE_URL}/system-rule-groups`;

  /**
   * Get all rule groups for current tenant
   */
  async getAll(): Promise<SystemRuleGroupMaintenanceDto[]> {
    const response = await AuthService.authenticatedGet(this.baseUrl);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Get a specific rule group by RuleGroupID
   */
  async getByRuleGroupId(ruleGroupId: number): Promise<SystemRuleGroupMaintenanceDto | null> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/${ruleGroupId}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Create a new rule group
   */
  async create(request: CreateSystemRuleGroupRequest): Promise<SystemRuleGroupMaintenanceDto> {
    const response = await AuthService.authenticatedPost(this.baseUrl, request);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Update an existing rule group
   */
  async update(ruleGroupId: number, request: UpdateSystemRuleGroupRequest): Promise<SystemRuleGroupMaintenanceDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${ruleGroupId}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Delete a rule group
   */
  async delete(ruleGroupId: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${ruleGroupId}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
  }

  /**
   * Search rule groups by name
   */
  async search(term: string): Promise<SystemRuleGroupMaintenanceDto[]> {
    const response = await AuthService.authenticatedGet(
      `${this.baseUrl}/search?term=${encodeURIComponent(term)}`
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  /**
   * Check if rule group has associated rules
   */
  async hasAssociatedRules(ruleGroupId: number): Promise<{ hasRules: boolean; ruleCount: number }> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/${ruleGroupId}/has-rules`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Ukjent feil' }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }
}

export const systemRuleGroupMaintenanceService = new SystemRuleGroupMaintenanceService();
export default systemRuleGroupMaintenanceService;
